import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  concurrentModuleState,
  getClinicModules,
  updateClinicModuleState,
  type ClinicModuleCatalogueItem,
  type ClinicModuleState,
} from '@/shared/api/clinicModulesClient'
import { defineCoreErrorResolution, resolveCoreError } from '@/shared/api/errors'
import type { MessageId } from '@/shared/i18n/catalogue.en'

import { desiredState, type ClinicModuleAct } from './moduleState'

/**
 * The Connected Modules catalogue as React Query state (tracker #202).
 *
 * ONE READ, ONE COMMAND, and a strict reading of what each may do to the
 * cache. The read fills the list. The command replaces exactly the item the
 * server returned — the whole item, because the response is the committed
 * truth and a merge would keep a field the server just changed — and only
 * AFTER the server answered; nothing here writes a desired state into the
 * cache ahead of the response. The list is then asked again, so the other
 * rows can catch up with a change someone else made in the meantime; if that
 * refresh fails the replaced item stays, because it came from the server,
 * and the page says the rest may be out of date.
 *
 * A stale write (`module_state_changed_concurrently`) is the one failure
 * that also carries truth: the server states the current lifecycle, and it is
 * written into the item so the operator sees what they are now deciding
 * against. The rest of that item — health, last change — is refreshed by the
 * same list read the caller issues next.
 */
export const CLINIC_MODULES_QUERY_KEY = ['control-center', 'modules'] as const

export function clinicModulesQueryOptions() {
  return queryOptions({
    queryKey: CLINIC_MODULES_QUERY_KEY,
    queryFn: ({ signal }): Promise<readonly ClinicModuleCatalogueItem[]> =>
      getClinicModules(signal),
    retry: false,
  })
}

export function useClinicModules(enabled = true) {
  return useQuery({ ...clinicModulesQueryOptions(), enabled })
}

/**
 * The operator's explicit act, as sent: which module, which of the four
 * transitions, the revision they saw when they chose it, the words they gave
 * and the key that makes one press one server-side transition however many
 * times the request itself is sent.
 */
export interface ClinicModuleStateChange {
  readonly moduleCode: string
  readonly act: ClinicModuleAct
  readonly revision: number
  readonly reason: string
  readonly idempotencyKey: string
}

function replaceItem(
  list: readonly ClinicModuleCatalogueItem[] | undefined,
  item: ClinicModuleCatalogueItem,
): readonly ClinicModuleCatalogueItem[] | undefined {
  return list?.map((entry) => (entry.moduleCode === item.moduleCode ? item : entry))
}

function replaceState(
  list: readonly ClinicModuleCatalogueItem[] | undefined,
  moduleCode: string,
  state: ClinicModuleState,
): readonly ClinicModuleCatalogueItem[] | undefined {
  return list?.map((entry) => (entry.moduleCode === moduleCode ? { ...entry, state } : entry))
}

export function useChangeClinicModuleState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (change: ClinicModuleStateChange) =>
      updateClinicModuleState(
        change.moduleCode,
        { ...desiredState(change.act), revision: change.revision, reason: change.reason },
        change.idempotencyKey,
      ),
    retry: false,
    onSuccess: (item) => {
      queryClient.setQueryData<readonly ClinicModuleCatalogueItem[]>(
        CLINIC_MODULES_QUERY_KEY,
        (list) => replaceItem(list, item),
      )
    },
    onError: (error, change) => {
      const current = concurrentModuleState(error)
      if (current) {
        queryClient.setQueryData<readonly ClinicModuleCatalogueItem[]>(
          CLINIC_MODULES_QUERY_KEY,
          (list) => replaceState(list, change.moduleCode, current),
        )
      }
    },
  })
}

/**
 * The words for a refused change. By code first — the two module conflicts
 * need different sentences and share one kind and one status — then by kind.
 * Every sentence says what did NOT happen, because the display the operator
 * is looking at is still the last committed state and must be believed.
 */
const STATE_CHANGE_FAILURE = defineCoreErrorResolution<MessageId>('clinicModules.error.failed', {
  byCode: {
    module_state_changed_concurrently: 'clinicModules.error.concurrent',
    module_hidden: 'clinicModules.error.hiddenCannotEnable',
    module_not_registered: 'clinicModules.error.notFound',
  },
  byKind: {
    unauthenticated: 'shell.sessionExpired',
    forbidden: 'clinicModules.error.forbidden',
    'not-found': 'clinicModules.error.notFound',
    'invalid-input': 'clinicModules.error.invalid',
    unavailable: 'clinicModules.error.unavailable',
    malformed: 'clinicModules.error.malformed',
  },
})

export function stateChangeFailureMessage(error: unknown): MessageId {
  return resolveCoreError(error, STATE_CHANGE_FAILURE)
}

const STATE_CHANGE_RETRY = defineCoreErrorResolution<boolean>(true, {
  byCode: {
    module_state_changed_concurrently: false,
    module_hidden: false,
    module_not_registered: false,
  },
  byKind: {
    unauthenticated: false,
    forbidden: false,
    'not-found': false,
    'invalid-input': false,
    conflict: false,
  },
})

/**
 * Whether a refused change can be sent again AS THE SAME ACT. A stale write
 * cannot: the state it was decided against is gone, and the operator must
 * choose again from the state the server stated. A refusal of the account or
 * of the module's existence cannot either. Everything else — the server
 * unreachable, a 5xx, a body the client could not read — is the same act
 * asked once more with the same key.
 */
export function stateChangeRetryable(error: unknown): boolean {
  return resolveCoreError(error, STATE_CHANGE_RETRY)
}

/**
 * The predefined reasons each act offers, in the order shown. A reason is
 * recorded in the clinic's audit trail with the change (tracker #199), so the
 * presets are the ordinary administrative causes and "other" always stands
 * beside them for the one that is not.
 */
export type ClinicModuleReasonId =
  | 'rollout'
  | 'maintenance'
  | 'maintenanceDone'
  | 'configuration'
  | 'notUsed'
  | 'postponed'

export const CLINIC_MODULE_REASON_LABEL: Record<ClinicModuleReasonId, MessageId> = {
  rollout: 'clinicModules.reason.rollout',
  maintenance: 'clinicModules.reason.maintenance',
  maintenanceDone: 'clinicModules.reason.maintenanceDone',
  configuration: 'clinicModules.reason.configuration',
  notUsed: 'clinicModules.reason.notUsed',
  postponed: 'clinicModules.reason.postponed',
}

export const CLINIC_MODULE_REASONS: Record<ClinicModuleAct, readonly ClinicModuleReasonId[]> = {
  enable: ['rollout', 'maintenanceDone'],
  disable: ['maintenance', 'configuration', 'notUsed'],
  hide: ['notUsed', 'postponed'],
  unhide: ['rollout'],
}

/** The contract's bound on the recorded reason (`UpdateClinicModuleStateRequest.reason`). */
export const CLINIC_MODULE_REASON_MAX_LENGTH = 500
